import {useState, useCallback} from 'react'
import {useApiService} from "./useApiService";
import {AxiosInstance} from "axios";

// Define the state interface using a generic type T
interface UseAsyncState<T> {
    data: T | null
    loading: boolean
    error: string | null
    isSuccess: boolean
}

// The return type of our hook, also generic
interface UseAsyncReturn<T, A extends any[]> extends UseAsyncState<T> {
    execute: (...args: A) => Promise<T | void>
    setData: (data: T | null) => void
    setError: (error: string | null) => void
}

// The hook itself is a generic function <T>
// It takes an async function that returns a Promise<T>
export const useAsync = <T, A extends any[]>(
    asyncFunction: (client: AxiosInstance, ...args: A) => Promise<T>,
    withAuth: boolean = false,
    handleFeedback: boolean = false,
    loading: boolean = true,
): UseAsyncReturn<T, A> => {
    const [state, setState] = useState<UseAsyncState<T>>({
        data: null,
        loading: loading,
        error: null,
        isSuccess: false
    })
    const apiService = useApiService()

    const execute = useCallback(async (...args: A): Promise<T | void> => {
        try {
            const client = await apiService.client(withAuth, handleFeedback)
            setState(prev => ({...prev, loading: true, error: null, isSuccess: false, data: null}))
            const result = await asyncFunction(client, ...args)
            setState({
                data: result,
                loading: false,
                error: null,
                isSuccess: true
            })
            return result
        } catch (error) {
            const errorMessage = error?.message || 'unknownErrorMessage'
            setState({
                data: null,
                loading: false,
                error: errorMessage,
                isSuccess: false
            })
        }
    }, [asyncFunction])

    const setData = useCallback((data: T | null) => {
        setState(prev => ({...prev, data}))
    }, [])

    const setError = useCallback((error: string | null) => {
        setState(prev => ({...prev, error}))
    }, [])

    return {
        execute,
        setData,
        setError,
        ...state
    }
}


export const useLegacyAsync = <T, A extends any[]>(
    asyncFunction: (...args: A) => Promise<T>,
    immediate?: boolean
): UseAsyncReturn<T, A> => {
    const [state, setState] = useState<UseAsyncState<T>>({
        data: null,
        loading: immediate,
        error: null,
        isSuccess: false
    });

    const execute = useCallback(async (...args: any[]): Promise<T | void> => {
        try {
            setState(prev => ({...prev, loading: true, error: null, isSuccess: false, data: null}));
            // @ts-ignore
            const result = await asyncFunction(...args);
            setState({
                data: result, // TypeScript knows 'result' is of type T
                loading: false,
                error: null,
                isSuccess: true
            });
            return result;
        } catch (error) {
            const errorMessage = error?.message || 'unknownErrorMessage';
            setState({
                data: null,
                loading: false,
                error: errorMessage,
                isSuccess: false
            });
        }
    }, [asyncFunction])

    const setData = useCallback((data: T | null) => {
        setState(prev => ({...prev, data}));
    }, []);

    const setError = useCallback((error: string | null) => {
        setState(prev => ({...prev, error}));
    }, []);

    return {
        execute,
        setData,
        setError,
        ...state
    }
}